// SelectCompetition/index.tsx
import React from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { useTRPC } from "@/trpc";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { useDebounce } from "@uidotdev/usehooks";
import { useVirtualizer } from "@tanstack/react-virtual";

interface Props {
  value?: string;
  label?: string;
  onValueChange?: (value: string | undefined, label?: string) => void; 
  disabled?: boolean;
  searchOptions?: {
    club?: { club_id?: string };
  };
}

const SelectCompetition = ({ value, label, onValueChange, disabled, searchOptions }: Props) => {
  const { trpc } = useTRPC();

  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [selectedValue, setSelectedValue] = React.useState<string | undefined>(value);
  React.useEffect(() => setSelectedValue(value), [value]);

  const optionsKey = React.useMemo(() => JSON.stringify(searchOptions ?? {}), [searchOptions]);
  const prevOptionsKeyRef = React.useRef(optionsKey);
  const optionsChangedRef = React.useRef(false);
  React.useEffect(() => {
    optionsChangedRef.current = optionsKey !== prevOptionsKeyRef.current;
    prevOptionsKeyRef.current = optionsKey;
  }, [optionsKey]);

  const queryKey = React.useMemo(
    () => ["competitions", debouncedSearch || "", optionsKey, "all"],
    [debouncedSearch, optionsKey]
  );

  const { data, isFetching, isError, error /*, refetch*/ } = useQuery({
    queryKey,
    enabled: open,
    keepPreviousData: true,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      trpc.competitions.search.query({
        search: debouncedSearch || undefined,
        club: searchOptions?.club?.club_id ? { club_id: searchOptions.club.club_id } : undefined,
        onlyCurrentSeason: true,
        all: true,
      }),
  });

  React.useEffect(() => {
    if (isError && error instanceof Error) toast.error(error.message);
  }, [isError, error]);

  React.useEffect(() => {
    if (!open || isFetching || selectedValue === undefined || !optionsChangedRef.current) return;
    const exists = data?.competitions?.some((c: any) => String(c.id) === String(selectedValue));
    if (!exists) {
      setSelectedValue(undefined);
      onValueChange?.(undefined);
    }
    optionsChangedRef.current = false;
  }, [open, isFetching, data, selectedValue, onValueChange]);

  const selectedObj = data?.competitions?.find((c: any) => c.id === selectedValue);
  const selectedLabel = selectedObj ? (selectedObj.country ? `${selectedObj.country} - ` : "") + selectedObj.name : undefined;

  const displayLabel = label ?? selectedLabel;

  const handlePick = React.useCallback(
    (id: string | undefined) => {
      if (selectedValue === id) {
        setSelectedValue(undefined);
        onValueChange?.(undefined, undefined);
      } else {
        const selectedObj = data?.competitions?.find((c: any) => c.id === id);
        const label = selectedObj ? (selectedObj.country ? `${selectedObj.country} - ` : "") + selectedObj.name : undefined;
        setSelectedValue(id);
        onValueChange?.(id, label);
        console.log(label);
      }
      setOpen(false);
    },
    [selectedValue, onValueChange, data]
  );

  const rows = data?.competitions ?? [];
  const parentRef = React.useRef<HTMLDivElement | null>(null);
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 48,
    overscan: 8,
  });

  React.useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => rowVirtualizer.measure());
    return () => cancelAnimationFrame(id);
  }, [open, rowVirtualizer]);

  if (disabled) {
    return (
      <Button variant="outline" className="w-full" disabled>
        {selectedLabel || "Competitie vastgezet"}
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full">
          {displayLabel || "Selecteer competitie..."}
        </Button>
      </DialogTrigger>

      <DialogContent className="p-0 rounded-3xl gap-0 overflow-hidden" hideCloseButton>
        <div className="flex items-center relative">
          <input
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder="Zoek naar competitie"
            className="focus:outline-none py-3 px-4 bg-input/50 rounded-3xl text-sm w-full h-12"
          />
          <button
            onClick={() => setOpen(false)}
            className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground size-12 aspect-square hover:bg-muted rounded-full flex items-center justify-center"
          >
            <Icons.X className="w-4 h-4" />
          </button>
        </div>

        {isFetching && <p className="text-center text-sm text-muted-foreground my-2">Bezig met zoeken...</p>}

        <div ref={parentRef} className="max-h-96 overflow-auto relative">
          <div style={{ height: rowVirtualizer.getTotalSize(), position: "relative" }} className="w-full">
            {rowVirtualizer.getVirtualItems().map((v) => {
              const c = rows[v.index];
              const isSelected = c?.id === selectedValue;
              return (
                <div
                  key={c.id}
                  data-index={v.index}
                  className="absolute top-0 left-0 w-full"
                  style={{ transform: `translateY(${v.start}px)` }}
                >
                  <button
                    onClick={() => handlePick(c.id)}
                    className={`flex items-center justify-between gap-4 p-2 rounded-xl hover:bg-muted text-left ${
                      isSelected ? "bg-muted/60" : ""
                    }`}
                  >
                    <div>
                      <p className="font-medium">{c.country ? `${c.country} - ` : ""}{c.name}</p>
                    </div>
                    {isSelected && <Icons.Check className="w-4 h-4" />}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SelectCompetition;
